import { RootReducer } from 'redux/reducers';
import * as S from './styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Conversation from 'components/Conversation';
import Icon, { IconType } from 'components/Icon';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import { useTheme } from 'styled-components';
import { useMemo, useEffect, useCallback, useState } from 'react';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { CHAT_COLUMN_GAP, ChatStatus } from 'dragonmouse-shared/interfaces';
import { setActiveChatId, setCenteredFrameId, setSidebarVisible } from 'redux/mode/actions';
import { getRightmostFrameX } from 'redux/db/util';
import { selectActiveScene, selectChats, selectActiveChat } from 'redux/db/reducers';
import forge from 'apis/forge';
import { v4 as uuid } from 'uuid';
import { displayViewOnlyModal } from 'redux/mode/util';
import { logout } from 'utils';
import FrameGallery from 'components/FrameGallery';
import ShareSceneModal from 'components/Modal/Share/ShareSceneModal';

const WorkspaceMobile = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [showShareModal, setShowShareModal] = useState(false);

	const { activeChatId, activeScene, chats, activeChat, centeredFrameId } = useSelector(
		(r: RootReducer) => {
			return {
				activeChatId: r.mode.activeChatId,
				activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
				chats: selectChats(r.db),
				activeChat: selectActiveChat(r.db, r.mode.activeChatId),
				centeredFrameId: r.mode.centeredFrameId,
			};
		},
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onNewChat = useCallback(async () => {
		if (!activeScene) return;
		if (activeScene.viewOnly) {
			displayViewOnlyModal();
			return;
		}

		const chatId = uuid();
		const oldActiveChatId = activeChatId;
		const rightmostFrameX = getRightmostFrameX();
		dispatch(setActiveChatId(chatId));

		try {
			await forge.createChat({
				id: chatId,
				sceneId: activeScene.id,
				title: NEW_CHAT_TITLE,
				x: rightmostFrameX !== null ? rightmostFrameX + CHAT_COLUMN_GAP : 0,
				y: 0,
				metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
			});
		} catch (e) {
			dispatch(setActiveChatId(oldActiveChatId));
			throw e;
		}
	}, [activeScene, activeChatId, dispatch]);

	// Filter active scene's chats and sort by updatedAt
	// (electric only has active scene's chats so no need to filter by scene)
	const sortedChats = useMemo(() => {
		const sorted = [...chats].sort((a, b) => {
			return b.updatedAt.getTime() - a.updatedAt.getTime();
		});
		return sorted;
	}, [chats]);

	// Auto-select first chat if no active chat is set (same logic as Chats component)
	useEffect(() => {
		if (!activeChatId || !sortedChats.some((chat) => chat.id === activeChatId)) {
			const firstChat = sortedChats.length > 0 ? sortedChats[0] : null;
			if (firstChat) {
				dispatch(setActiveChatId(firstChat.id));
			}
		}
	}, [sortedChats, activeChatId, dispatch]);

	// Create menu items for chats dropdown
	const chatMenuItems = useMemo(() => {
		const items = [
			new MenuItemAction('Create new chat...', '', () => {
				onNewChat();
			}),
			new MenuItemDivider(),
		];

		if (sortedChats.length > 0) {
			items.push(
				...sortedChats.map((chat) => {
					const isActive = chat.id === activeChatId;
					return new MenuItemAction(
						chat.title || 'Untitled Chat',
						'',
						() => {
							dispatch(setActiveChatId(chat.id));
							dispatch(setSidebarVisible(false)); // Close sidebar when chat is selected
						},
						false,
						isActive // showCheckmark
					);
				})
			);
		}

		return items;
	}, [sortedChats, activeChatId, dispatch, onNewChat]);

	const moreMenuItems = [
		MenuItemAction.createReturnToProjectList(),
		new MenuItemAction('Log out', '', () => {
			logout();
		}),
		...(activeChatId && !activeScene?.viewOnly
			? [MenuItemAction.createRenameChat(activeChatId, false)]
			: []),
	];

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base>
			<S.Application>
				<S.Header>
					<S.HeaderLeft>
						<SidebarToggle />
						{activeChat && (
							<DropdownMenu menuItems={chatMenuItems}>
								<S.ChatTitleButton>
									<S.ChatTitle>{activeChat.title || 'Untitled Chat'}</S.ChatTitle>
									<S.ChatTitleIcon>
										<Icon
											type={IconType.FILLED_ARROW_DOWN}
											width={16}
											height={16}
											color={theme.gray['800']}
										/>
									</S.ChatTitleIcon>
								</S.ChatTitleButton>
							</DropdownMenu>
						)}
					</S.HeaderLeft>
					<S.HeaderRight>
						<S.HeaderButton onClick={() => setShowShareModal(true)}>
							<Icon type={IconType.SHARE} width={24} height={24} color={theme.gray['1000']} />
						</S.HeaderButton>
						<DropdownMenu menuItems={moreMenuItems}>
							<S.MoreButton>
								<S.InnerWrapper>
									<S.MoreButtonLogo>
										<Icon type={IconType.MORE2} width={24} height={24} color={theme.gray['1000']} />
									</S.MoreButtonLogo>
								</S.InnerWrapper>
							</S.MoreButton>
						</DropdownMenu>
					</S.HeaderRight>
				</S.Header>

				<S.MainContent>
					{activeChatId ? (
						<Conversation />
					) : (
						<S.Placeholder>
							<S.PlaceholderIcon>
								<Icon type={IconType.CHAT} width={48} height={48} color={theme.gray['400']} />
							</S.PlaceholderIcon>
							<S.PlaceholderTitle>Start a new conversation</S.PlaceholderTitle>
							<S.PlaceholderDescription>
								Tap "New Chat" to begin designing with AI
							</S.PlaceholderDescription>
						</S.Placeholder>
					)}
				</S.MainContent>
			</S.Application>

			{centeredFrameId && (
				<FrameGallery
					frameId={centeredFrameId}
					sceneControls={true}
					onClose={() => dispatch(setCenteredFrameId(null))}
				/>
			)}
			{showShareModal && activeScene && (
				<ShareSceneModal scene={activeScene} onClose={() => setShowShareModal(false)} />
			)}
		</S.Base>
	);
};

export default WorkspaceMobile;
